"use client"

import { type ComponentProps, useEffect, useState } from "react"

import { useBillingPoints } from "@/contexts/BillingPointContext"
import type { BillingPoint } from "@/types/billingPoint"

import Selector, { type SelectorItem } from "./Selector"

type Props = Omit<ComponentProps<typeof Selector>, "items"> & {
  retrieveContracts?: boolean
  totalCount?: number
  setBillingPoints?: (bps: BillingPoint[]) => void
  initialBillingPoints?: BillingPoint[]
  items?: SelectorItem[]
  excludeDisabled?: boolean
}

const BillingpointSelector = ({
  retrieveContracts = true,
  initialBillingPoints = [],
  setBillingPoints,
  items,
  excludeDisabled = false,
  ...props
}: Props) => {
  const {
    billingPoints: contextBillingPoints,
    loading,
    hasData
  } = useBillingPoints()
  const [billingPoints, setLocalBillingPoints] = useState<BillingPoint[]>(
    initialBillingPoints || []
  )

  useEffect(() => {
    // If we have initial billing points and don't need to retrieve contracts,
    // use the initial data and don't fetch from context
    if (
      initialBillingPoints &&
      initialBillingPoints.length > 0 &&
      !retrieveContracts
    ) {
      const next = excludeDisabled
        ? initialBillingPoints.filter(
            (bp) => bp.Contracts && bp.Contracts?.length > 0
          )
        : initialBillingPoints
      setLocalBillingPoints(next)
      // Don't call setBillingPoints here to avoid overriding filtered data
    }
    // Otherwise, use context data if available
    else if (hasData && contextBillingPoints.length > 0) {
      const next = excludeDisabled
        ? contextBillingPoints.filter(
            (bp: BillingPoint) => bp.Contracts && bp.Contracts?.length > 0
          )
        : contextBillingPoints
      setLocalBillingPoints(next)
      setBillingPoints?.(next)
    }
    // Fallback to initial props if context not ready
    else if (initialBillingPoints && initialBillingPoints.length > 0) {
      const next = excludeDisabled
        ? initialBillingPoints.filter(
            (bp) => bp.Contracts && bp.Contracts?.length > 0
          )
        : initialBillingPoints
      setLocalBillingPoints(next)
    }
  }, [
    contextBillingPoints,
    hasData,
    excludeDisabled,
    setBillingPoints,
    initialBillingPoints,
    retrieveContracts
  ])

  const baseItems = items
    ? items
    : convertBillingPointsToSelectorItems(billingPoints)

  return <Selector {...props} items={baseItems} loading={loading} />
}

export const convertBillingPointsToSelectorItems = (
  billingPoints: BillingPoint[]
): SelectorItem[] => {
  return billingPoints.map((bp) => ({
    value: bp?.BillingPointsId?.toString() || "",
    title:
      bp?.installationaddress ||
      bp?.PrimaryInfo ||
      bp?.ExternalId ||
      `#${bp?.BillingPointsId ?? ""}`,
    triggerTitle:
      bp?.installationaddress ||
      bp?.PrimaryInfo ||
      bp?.ExternalId ||
      `#${bp?.BillingPointsId ?? ""}`,
    subtitle: bp?.ExternalId || "&nbsp;",
    tag: bp?.production ? "Produktion" : "Förbrukning",
    status: bp?.active ? "Aktiv" : "Avslutad"
  }))
}

export default BillingpointSelector
